import { SwitchType } from "@components/inputs/switch/Switch";
import { not, TGetValueFn } from "@components/smart/FieldInfo";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    CompanyEntity,
    EntitySetName,
    EntityTypeName,
    PrCsszHzupnSubmissionCompanyEntity,
    PrCsszHzupnSubmissionDetailEntity,
    PrCsszHzupnSubmissionEntity,
    PrCsszHzupnSubmissionWorkedIntervalEntity,
    YesNoEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionTypeCode, YesNoCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import PrCsszHzupnSubmissionFormView from "@pages/payroll/submission/submissionTypes/PrCsszHzupnSubmissionFormView";
import {
    addCSSZSwitchDef,
    addEmployeeMasterField,
    addEmployerDefinitions,
    CSSZTranslationFiles
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType } from "../../../../enums";
import { getUtcDayjs } from "../../../../types/Date";
import { addFormDef, addFormGroup } from "../../../../views/formView/Form.utils";

export const hasWorkIntervalsLocalPath = BindingContext.localContext("hasWorkIntervals");

function isOfStartedEmploymentType(type: YesNoCode): TGetValueFn<boolean> {
    return (args) => {
        const path = createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.HasEmployeeStartedEmploymentCode);
        return args.storage.getValueByPath(path) === type;
    };
}

const hasStartedEmployment = isOfStartedEmploymentType(YesNoCode.Yes);
const hasNotStartedEmployment = isOfStartedEmploymentType(YesNoCode.No);

const isForeigner: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.IsForeigner));
};

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCsszHzupnSubmissionFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork,
        subType: null
    }));

    d.form.additionalProperties = [
        ...d.form.additionalProperties,
        { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.Company, PrCsszHzupnSubmissionCompanyEntity.Company, CompanyEntity.Id) }
    ];

    // **************** identifier group ****************
    addCSSZSwitchDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.IsForeigner), null, [
        createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DecisionNumber)
    ]);
    addCSSZSwitchDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.IsCorrectiveSubmission));
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DecisionNumber), {
        label: i18next.t("Submission:SocialEventsForm.DPNDecisionNumber"),
        width: BasicInputSizes.XL,
        isRequired: not(isForeigner),
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.Identifier,
        title: i18next.t("Submission:SocialEventsForm.Identifier"),
        rows: [[
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.IsForeigner) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.IsCorrectiveSubmission) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DecisionNumber) },
        ]]
    });

    // **************** Add company / employer fields ****************
    addEmployerDefinitions(d);

    // **************** Add Employee related fields ****************
    addEmployeeMasterField(d, {
        isSingleEmployeeView: args?.isSingleEmployeeView,
        withoutEmployeeContact: true,
        separateEmployeeIdentifiers: true
    });

    // **************** Add Submission fields ****************
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.HasEmployeeStartedEmployment), {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XL,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        affectedFields: [
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.EmployeeNotStartedEmploymentReason) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DateEmployeeStartedEmployment) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedHoursOnLastIllnessDay) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.ShiftLengthOnLastIllnessDay) },
        ]
    });
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.EmployeeNotStartedEmploymentReason), {
        type: FieldType.EditableText,
        isRequired: true,
        isVisible: hasNotStartedEmployment,
        fieldSettings: {}
    });
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DateEmployeeStartedEmployment), {
        label: i18next.t("Submission:HZUPNForm.DateEmployeeStartedEmployment"),
        isRequired: true,
        isVisible: hasStartedEmployment
    });

    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedHoursOnLastIllnessDay), {
        width: BasicInputSizes.XL,
        isRequired: true,
        isVisible: hasStartedEmployment,
        fieldSettings: {
            unit: "h"
        }
    });
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.ShiftLengthOnLastIllnessDay), {
        width: BasicInputSizes.XL,
        isRequired: true,
        isVisible: hasStartedEmployment,
        fieldSettings: {
            unit: "h"
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.EmployerSubmission,
        title: i18next.t("Submission:HZUPNForm.Detail"),
        isSmallBottomMargin: true,
        rows: [[
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.HasEmployeeStartedEmployment) },
        ], [
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.EmployeeNotStartedEmploymentReason) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DateEmployeeStartedEmployment) },
        ], [
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedHoursOnLastIllnessDay) },
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.ShiftLengthOnLastIllnessDay) },
        ]]
    });

    // **************** Working intervals (line items) ****************
    addFormDef(d, hasWorkIntervalsLocalPath, {
        type: FieldType.Switch,
        label: i18next.t("Submission:HZUPNForm.WorkedIntervals"),
        defaultValue: false,
        fieldSettings: {
            type: SwitchType.YesNo
        },
    });
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedIntervals, PrCsszHzupnSubmissionWorkedIntervalEntity.DateFrom), {});
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedIntervals, PrCsszHzupnSubmissionWorkedIntervalEntity.DateTo), {});

    addFormGroup(d, {
        id: PrSubmissionGroup.WorkIntervals,
        isSmallBottomMargin: true,
        rows: [[
            { id: hasWorkIntervalsLocalPath },
        ]],
        lineItems: {
            collection: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.WorkedIntervals),
            order: PrCsszHzupnSubmissionWorkedIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(hasWorkIntervalsLocalPath),
            columns: [
                { id: PrCsszHzupnSubmissionWorkedIntervalEntity.DateFrom },
                { id: PrCsszHzupnSubmissionWorkedIntervalEntity.DateTo }
            ],
        }
    });
    // **************** Date issued footer ****************
    addFormDef(d, createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DateIssued), {
        defaultValue: () => getUtcDayjs()
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AdditionalInfo,
        rows: [[
            { id: createPath(PrCsszHzupnSubmissionEntity.Detail, PrCsszHzupnSubmissionDetailEntity.DateIssued) }
        ]]
    });

    return {
        ...d,
        entitySet: EntitySetName.PrCsszHzupnSubmissions
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCsszHzupnSubmission, getDefinitions);